import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';

interface HtmlViewerProps {
  fileUrl: string;
  fileName: string;
  onError?: () => void;
}

const HtmlViewer: React.FC<HtmlViewerProps> = ({
  fileUrl,
  fileName,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    let isActive = true;

    const checkFile = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        if (isActive) {
          setLoading(false);
        }
      } catch (err) {
        if (isActive) {
          setError('파일을 불러올 수 없습니다.');
          setLoading(false);
          onError?.();
        }
        console.error(err);
      }
    };

    checkFile();

    return () => {
      isActive = false;
    };
  }, [fileUrl, onError]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#FFFFFF' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#FFFFFF',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        {error}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        // 모바일: 여백 없이 콘텐츠를 꽉 차게
        padding: isMobile ? 0 : '32px 48px',
        boxSizing: 'border-box',
      }}
    >
      <iframe
        src={fileUrl}
        // 첨부 HTML을 격리된 sandbox에서 렌더링.
        // allow-same-origin을 제거해 iframe을 고유한 불투명(opaque) 출처로 격리한다.
        // 이렇게 하면 업로드된 HTML 스크립트가 파일 호스트 출처로 실행되어
        // 같은 호스트의 다른 첨부를 읽어 외부로 유출하는 것을 차단한다.
        // allow-scripts는 스크립트 포함 HTML도 정상 렌더링되도록 유지한다.
        // 단순 미리보기이므로 popups/forms 등 추가 권한은 부여하지 않는다.
        sandbox="allow-scripts"
        // iframe 자체 로드 실패도 오류로 처리
        onError={() => {
          setError('파일을 불러올 수 없습니다.');
          onError?.();
        }}
        style={{
          borderRadius: isMobile ? 0 : '8px',
          border: isMobile
            ? 'none'
            : '1px solid var(--GrayScale-gray_3, #393D41)',
          background: 'var(--GrayScale-gray_White, #2B2D2F)',
          boxShadow: isMobile
            ? 'none'
            : '0 0 16px 0 var(--effect-black-16, rgba(255, 255, 255, 0.16))',
          width: '100%',
          height: '100%',
        }}
        title={fileName}
      />
    </Box>
  );
};

export default HtmlViewer;
